import { create } from "zustand";

// Mirrors the chip shape used by AgentChat's local mentionChips state.
// Keep this in sync with the MentionChip type in
// `apps/web-vite/src/components/agent/mention-dropdown.tsx`.
export interface MentionChip {
  type: "project" | "file" | "theme" | "skill";
  name: string;
  id?: string;
}

// External injection seam for the agent input.
//
// Today, AgentChat's mentionChips live in local React useState — no external
// component can push chips into it. Rather than refactor the whole input
// model, this store provides a one-way queue that AgentChat subscribes to and
// drains on every change.
//
// Why subscribe-on-change (not consume-on-mount): if AgentChat is already
// mounted when an external trigger fires `injectFileMention`, a mount-time
// consume would miss the push. Subscribing to `pendingChips` catches both the
// "panel just opened, drain queue" and "panel already open, inject runs at
// runtime" cases with the same effect.
//
// Why a separate `shouldOpenPanel` flag: `IntegratedEditorWithAgent` owns
// viewMode and is the only thing that can flip it. It watches this flag,
// flips viewMode to 'agent', and clears the flag — same Zustand action also
// pushes the chip, so the synchronous flush guarantees AgentChat mounts with
// a non-empty queue ready to drain.
interface AgentInputStore {
  pendingChips: MentionChip[];
  shouldOpenPanel: boolean;

  enqueueChip: (chip: MentionChip) => void;
  drainChips: () => MentionChip[];
  clearOpenSignal: () => void;

  // Compound action — the only entry point most callers need.
  // Pushes a `file` chip if not already queued, requests panel open.
  injectFileMention: (path: string) => void;
}

export const useAgentInputStore = create<AgentInputStore>((set, get) => ({
  pendingChips: [],
  shouldOpenPanel: false,

  enqueueChip: (chip) =>
    set((s) => {
      const dup = s.pendingChips.some(
        (c) => c.type === chip.type && c.name === chip.name,
      );
      if (dup) return s;
      return { pendingChips: [...s.pendingChips, chip] };
    }),

  drainChips: () => {
    const chips = get().pendingChips;
    if (chips.length === 0) return chips;
    set({ pendingChips: [] });
    return chips;
  },

  clearOpenSignal: () => set({ shouldOpenPanel: false }),

  injectFileMention: (path) => {
    const existing = get().pendingChips;
    const dup = existing.some((c) => c.type === "file" && c.name === path);
    set({
      pendingChips: dup
        ? existing
        : [...existing, { type: "file", name: path }],
      shouldOpenPanel: true,
    });
  },
}));
